// What `npm create @estra` writes, as data: the estra.cloud dashboard uses the same starters, so
// the files a project is provisioned with and the files the scaffold writes are always the same.
// This module reads nothing from disk; the CLI (cli.ts) does the writing.
import { SHARED, STARTER_FILES, STARTER_IDS, STARTER_SCHEMA_JSON, VERSION, type StarterId } from "./starters.ts";

export { STARTER_IDS, VERSION, type StarterId };

export type Platform = "web" | "ios" | "android" | "server";
export const PLATFORMS: Platform[] = ["web", "ios", "android", "server"];

export interface Starter {
  id: StarterId;
  name: string;
  description: string;
}
export const STARTERS: Starter[] = [
  { id: "blank", name: "Blank", description: "A profiles collection keyed by user id, owner-only rules. Add your own collections from there." },
  { id: "social", name: "Social", description: "Profiles, posts and comments with public read and owner write: a query, an index, a subcollection and a live feed." },
];

export interface Options {
  /** The backend URL from the project page, e.g. https://my-app.estra.cloud. */
  url?: string;
  starter: StarterId;
  platforms: Platform[];
  /** A name for the local cache; defaults to the first label of the URL's host. */
  name?: string;
}

/** The starter's schema (as source and as JSON) and rules, as the dashboard uploads them when a fresh project is created. */
export function backendFiles(starter: StarterId): { "schema.ts": string; "schema.json": string; "firestore.rules": string } {
  const files = STARTER_FILES[starter];
  const schema = files["schema.ts"];
  const rules = files["firestore.rules"];
  if (!schema || !rules) throw new Error(`starter "${starter}" is missing schema.ts or firestore.rules`);
  return { "schema.ts": schema, "schema.json": JSON.stringify(STARTER_SCHEMA_JSON[starter]), "firestore.rules": rules };
}

const PLACEHOLDER_URL = "https://<your-project>.estra.cloud";

function slugOf(o: Options): string {
  if (o.name) return o.name;
  try {
    const host = new URL(o.url ?? "").hostname;
    return host.split(".")[0] || "estra";
  } catch {
    return "estra";
  }
}

function wanted(path: string, platforms: Platform[]): boolean {
  if (!path.startsWith("examples/")) return true;
  const area = path.split("/")[1];
  if (area === "web") return platforms.includes("web");
  if (area === "server") return platforms.includes("server");
  if (area === "mobile") return platforms.includes("ios") || platforms.includes("android");
  return false;
}

function readme(o: Options, url: string, files: string[]): string {
  const mobile = o.platforms.includes("ios") || o.platforms.includes("android");
  return [
    `# Estra backend files`,
    ``,
    `Your backend: ${url}`,
    `Project page: https://estra.cloud/app`,
    ``,
    `## Files`,
    `- \`schema.ts\`: your collections and fields. Edit it, then \`npx estra push\` (below) changes the backend in seconds.`,
    `- \`firestore.rules\`: who may read and write what. Same: edit, push.`,
    `- \`estra.json\`: the URL and the starter this folder came from.`,
    `- \`package.json\`: the push and rules-check tools. It's an ES module (\`"type": "module"\`), which schema.ts needs.`,
    `- \`.env.example\`: the variables \`estra push\` and your servers need. Copy to \`.env\` and add the service key from the project page; never commit it.`,
    ...(files.some((f) => f.startsWith("examples/web/")) ? [`- \`examples/web/\`: the client (\`estra.ts\`), a first write and listener (\`example.ts\`), and the page email links land on (\`auth-action.ts\`, serve it at /auth/action).`] : []),
    ...(files.some((f) => f.startsWith("examples/server/")) ? [`- \`examples/server/\`: \`@estra/admin-node\` with the service key, for servers and scripts.`] : []),
    ...(mobile ? [`- \`examples/mobile/\`: the Kotlin Multiplatform SDK (Android, and iOS through the Kotlin framework).`] : []),
    ``,
    `## Install`,
    `\`\`\`sh`,
    ...(o.platforms.includes("web") ? [`npm i @estra/client`] : []),
    ...(o.platforms.includes("server") ? [`npm i @estra/admin-node`] : []),
    `npm install                  # in this folder: estra push and the rules checker`,
    `npm run rules:check`,
    `\`\`\``,
    ``,
    `## Change the schema or rules`,
    `\`\`\`sh`,
    `npm run push:check          # shows the SQL it would run, changes nothing`,
    `npm run push                # applies it; removing data needs: npx --no estra push --allow-destructive`,
    `\`\`\``,
    ``,
    `## Sign-in`,
    `Email link and password sign-in work out of the box; the mail comes from no-reply@estra.cloud.`,
    `Google, Apple and GitHub are switched on from the project page.`,
    ``,
    `## Docs`,
    `https://estra.cloud/docs`,
    ``,
  ].join("\n");
}

/** Every file the scaffold writes, keyed by path relative to the target folder. */
export function planFiles(o: Options): Record<string, string> {
  if (!STARTER_IDS.includes(o.starter)) throw new Error(`unknown starter "${o.starter}" (${STARTER_IDS.join(", ")})`);
  if (!o.platforms.length) throw new Error("pick at least one platform");
  for (const p of o.platforms) if (!PLATFORMS.includes(p)) throw new Error(`unknown platform "${p}" (${PLATFORMS.join(", ")})`);
  const url = (o.url ?? PLACEHOLDER_URL).replace(/\/$/, "");
  const slug = slugOf(o);
  const out: Record<string, string> = {};
  for (const [path, text] of Object.entries({ ...SHARED, ...STARTER_FILES[o.starter] })) {
    if (wanted(path, o.platforms)) out[path] = text.split("__URL__").join(url).split("__SLUG__").join(slug);
  }
  out["estra.json"] = `${JSON.stringify({ url, starter: o.starter, starterVersion: VERSION, platforms: o.platforms }, null, 2)}\n`;
  out["package.json"] = `${JSON.stringify(
    {
      name: `${slug}-estra`,
      private: true,
      // schema.ts is an ES module; \`npm init -y\` would say "commonjs" and Node couldn't load it.
      type: "module",
      scripts: { push: "estra push", "push:check": "estra push --dry-run", "rules:check": "estra-rules check firestore.rules" },
      devDependencies: { "@estra/schema": `^${VERSION}`, "@estra/rules-firestore": `^${VERSION}` },
    },
    null,
    2,
  )}\n`;
  out[".gitignore"] = ".env\nnode_modules\n";
  out[".env.example"] = `# Your backend's URL (safe to ship) and its service key (servers only; from the project page).\nESTRA_URL=${url}\nESTRA_KEY=\n`;
  out["README.md"] = readme(o, url, Object.keys(out));
  return out;
}
